import { useRef } from "react";
import { motion, useMotionTemplate, useMotionValue, useSpring } from "framer-motion";

/**
 * Tilt3D — luxury mouse-tracking 3D tilt with moving spec-highlight.
 *
 * Wrap any product visual to give it dimensional weight.
 * Props: className (applied to outer perspective wrapper),
 *        max (max rotation in degrees, default 10),
 *        glare (0..1, opacity of moving highlight, default 0.35),
 *        rest (spring config)
 */
export const Tilt3D = ({ children, className = "", max = 10, glare = 0.35 }) => {
  const ref = useRef(null);

  // -1..1
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  // 0..100 (percent) for spec position
  const gx = useMotionValue(50);
  const gy = useMotionValue(50);
  // 0..1 for spec opacity (0 when idle, 1 when hovering)
  const gOpacity = useMotionValue(0);

  const spring = { stiffness: 140, damping: 18, mass: 0.7 };
  const sRx = useSpring(rx, spring);
  const sRy = useSpring(ry, spring);
  const sGx = useSpring(gx, spring);
  const sGy = useSpring(gy, spring);
  const sO = useSpring(gOpacity, { stiffness: 90, damping: 20 });

  // motion.div style="transform: perspective(...) rotateX rotateY"
  const rotateX = useMotionTemplate`${sRx}deg`;
  const rotateY = useMotionTemplate`${sRy}deg`;
  const glareBg = useMotionTemplate`radial-gradient(circle at ${sGx}% ${sGy}%, rgba(255,255,255,${glare}) 0%, rgba(255,255,255,0) 55%)`;

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width; // 0..1
    const py = (e.clientY - r.top) / r.height;
    // tilt so top-left = rotateX+, rotateY-
    ry.set((px - 0.5) * 2 * max);   // horizontal position → rotateY
    rx.set(-(py - 0.5) * 2 * max);  // vertical position → rotateX (invert)
    gx.set(px * 100);
    gy.set(py * 100);
    gOpacity.set(1);
  };

  const onLeave = () => {
    rx.set(0);
    ry.set(0);
    gOpacity.set(0);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className={className}
      style={{ perspective: "1200px" }}
    >
      <motion.div
        style={{
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
          willChange: "transform",
        }}
        className="relative w-full h-full"
      >
        {children}
        {/* moving spec highlight */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0 mix-blend-overlay"
          style={{ background: glareBg, opacity: sO }}
        />
      </motion.div>
    </div>
  );
};
